<script>
import Logo from './Logo.vue';
import PrimaryNavItem from './PrimaryNavItem.vue';
import OptionsMenu from './OptionsMenu.vue';
import AgentDetails from './AgentDetails.vue';
import NotificationBell from './NotificationBell.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { ACCOUNT_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';

export default {
  components: {
    Logo,
    PrimaryNavItem,
    OptionsMenu,
    AgentDetails,
    NotificationBell,
  },
  props: {
    isACustomBrandedInstance: {
      type: Boolean,
      default: false,
    },
    logoSource: {
      type: String,
      default: '',
    },
    installationName: {
      type: String,
      default: '',
    },
    accountId: {
      type: Number,
      default: 0,
    },
    menuItems: {
      type: Array,
      default: () => [],
    },
    activeMenuItem: {
      type: String,
      default: '',
    },
  },
  emits: ['toggleAccounts', 'openNotificationPanel', 'openKeyShortcutModal'],
  data() {
    return {
      showOptionsMenu: false,
      pendingTasksCount: 0,
      _tasksInterval: null,
    };
  },
  mounted() {
    this.cargarTasksCount();
    this._tasksInterval = setInterval(() => this.cargarTasksCount(), 60000);
  },
  beforeUnmount() {
    if (this._tasksInterval) clearInterval(this._tasksInterval);
  },
  computed: {
    _authHeaders() {
      const t = this.$store.getters.getCurrentUser?.access_token;
      return t ? { headers: { 'api_access_token': t } } : {};
    },
    mainMenuItems() {
      return this.menuItems.filter(menuItem => menuItem.label !== 'SETTINGS');
    },
    isPlatformSuperAdmin() {
      return this.$store.getters.getCurrentUser?.type === 'SuperAdmin';
    },
    settingsMenuItem() {
      if (!this.isPlatformSuperAdmin) return null;
      return this.menuItems.find(menuItem => menuItem.label === 'SETTINGS');
    },
  },
  methods: {
    frontendURL,
    async cargarTasksCount() {
      if (!this.accountId) return;
      try {
        const response = await window.axios.get(
          `/api/v1/accounts/${this.accountId}/tasks?status=pendiente`,
          this._authHeaders
        );
        this.pendingTasksCount = (response.data || []).filter(
          task => task.status === 'pendiente' || task.status === 'en_progreso'
        ).length;
      } catch (error) {
        this.pendingTasksCount = 0;
      }
    },
    toggleOptions() {
      this.showOptionsMenu = !this.showOptionsMenu;
    },
    toggleAccountModal() {
      this.$emit('toggleAccounts');
    },
    toggleSupportChatWindow() {
      window.$chatwoot.toggle();
    },
    openNotificationPanel() {
      useTrack(ACCOUNT_EVENTS.OPENED_NOTIFICATIONS);
      this.$emit('openNotificationPanel');
    },
  },
};
</script>

<template>
  <div
    class="ci-primary-nav flex h-full min-h-0 flex-col border-r rtl:border-l rtl:border-r-0"
  >
    <div class="ci-primary-logo flex shrink-0 items-center justify-center">
      <Logo
        :source="logoSource"
        :name="installationName"
        :account-id="accountId"
        class="ci-primary-logo-mark"
      />
    </div>

    <nav
      class="ci-primary-menu flex min-h-0 flex-1 flex-col items-center"
      aria-label="Módulos principales"
    >
      <PrimaryNavItem
        v-for="menuItem in mainMenuItems"
        :key="menuItem.toState"
        :icon="menuItem.icon"
        :name="menuItem.label"
        :to="menuItem.toState"
        :count="
          menuItem.label === 'TAREAS' && pendingTasksCount > 0
            ? String(pendingTasksCount)
            : ''
        "
        :is-child-menu-active="menuItem.key === activeMenuItem"
      />
    </nav>

    <div class="ci-primary-footer flex shrink-0 flex-col items-center">
      <PrimaryNavItem
        v-if="settingsMenuItem"
        :icon="settingsMenuItem.icon"
        :name="settingsMenuItem.label"
        :to="settingsMenuItem.toState"
        :is-child-menu-active="settingsMenuItem.key === activeMenuItem"
      />
      <NotificationBell @open-notification-panel="openNotificationPanel" />
      <AgentDetails @toggle-menu="toggleOptions" />
      <OptionsMenu
        :show="showOptionsMenu"
        @toggle-accounts="toggleAccountModal"
        @show-support-chat-window="toggleSupportChatWindow"
        @open-key-shortcut-modal="$emit('openKeyShortcutModal')"
        @close="toggleOptions"
      />
    </div>
  </div>
</template>

<style scoped>
.ci-primary-nav {
  width: 80px !important;
  min-width: 80px !important;
  max-width: 80px !important;
  height: 100%;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
  color: #fff;
  background: linear-gradient(180deg, #0f172a 0%, #134e4a 100%) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
  box-shadow: 4px 0 20px rgba(15, 23, 42, 0.12);
}

.ci-primary-logo {
  min-height: 104px;
  width: 100%;
  background: inherit;
}

.ci-primary-nav .ci-primary-logo .ci-primary-logo-mark {
  margin: 0 !important;
}

.ci-primary-menu {
  width: 100%;
  padding: 2px 4px 10px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.26) transparent;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}

.ci-primary-menu::-webkit-scrollbar {
  width: 4px;
}

.ci-primary-menu::-webkit-scrollbar-track {
  background: transparent;
}

.ci-primary-menu::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.24);
  border-radius: 999px;
}

.ci-primary-footer {
  width: 100%;
  padding: 8px 4px 12px;
  background: #134e4a;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 -10px 24px rgba(15, 23, 42, 0.14);
}

.ci-primary-footer :deep(button) {
  color: rgba(255, 255, 255, 0.88) !important;
}

.ci-primary-footer :deep(button:hover) {
  color: #fff !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

@media (max-height: 760px) {
  .ci-primary-logo {
    min-height: 76px;
  }

  .ci-primary-footer {
    padding-top: 4px;
    padding-bottom: 6px;
  }
}
</style>
